/**
 * AdminAnnouncementModal
 * ======================
 * Shows a super-admin Communication Center broadcast as a modal popup to users
 * who are connected to the website when it is sent. Offline users are unaffected
 * and still receive the existing push / email / WhatsApp message.
 *
 * Mounted ONCE, inside AppShell, so it covers every authenticated surface.
 *
 * Notes for future edits:
 *
 *  - It listens on `admin_announcement`, NOT the existing `notification` event.
 *    `notification` is handled by NotificationContext, which renders a toast and
 *    writes into the bell cache. Moving this onto that event would replace the
 *    required modal with a toast and inject a bell row with no server-side
 *    record (it would disappear on the next refetch).
 *
 *  - `useChat()` returns undefined when there is no ChatProvider above it — the
 *    /admin/* route tree has none. It is read defensively rather than
 *    destructured so mounting inside the shared AppShell cannot crash the admin
 *    pages; with no socket the component simply renders nothing.
 *
 *  - The body is rendered as TEXT. HTML email bodies are already flattened to
 *    plain text server-side (stripHtmlToText). Do NOT switch this to
 *    dangerouslySetInnerHTML — it would turn admin-authored email HTML into a
 *    live injection surface in every user's browser.
 */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Megaphone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/AuthProvider';
import { useChat } from '@/contexts/ChatContext';
import { NotificationContext } from '@/contexts/NotificationContext';
import {
  ADMIN_ANNOUNCEMENT_EVENT,
  AdminAnnouncement,
  announcementHeading,
  enqueueAnnouncement,
  isAnnouncementExpired,
  isAnnouncementForUser,
  isInternalActionUrl,
  isSafeActionUrl,
  parseAnnouncementPayload,
  recordModalShown,
  shouldOpenModalForFrequency,
  shouldShowAnnouncement,
} from '@/lib/notifications/adminAnnouncement';
import {
  InAppSeverity,
  SEVERITY_PRESENTATION,
  inAppAnnouncementOf,
  isForCurrentSurface,
  isInAppAnnouncementExpired,
} from '@/lib/notifications/inAppNotification';

/** Icon per severity. `critical` and `warning` share the shape; the colour separates them. */
const SEVERITY_ICON: Record<InAppSeverity, React.ComponentType<{ className?: string }>> = {
  info: Megaphone,
  success: CheckCircle2,
  warning: AlertTriangle,
  critical: AlertTriangle,
};

export const AdminAnnouncementModal: React.FC = () => {
  const { user } = useAuth();
  // Defensive: no ChatProvider on /admin/* routes (see header note).
  const chat = useChat();
  const socket = chat?.socket ?? null;
  const isConnected = chat?.isConnected ?? false;
  const navigate = useNavigate();

  const [current, setCurrent] = useState<AdminAnnouncement | null>(null);
  const [queue, setQueue] = useState<AdminAnnouncement[]>([]);
  const seenIdsRef = useRef<Map<string, number>>(new Map());

  // Defensive, exactly like `useChat` above: the /admin/* tree has no
  // NotificationProvider, and this component is mounted in the shared AppShell.
  // No provider simply means no replay — the live socket path is unaffected.
  const notificationContext = useContext(NotificationContext);
  const notifications = notificationContext?.notifications;

  /**
   * Announcements this MOUNT has already replayed.
   *
   * 🔴 This is what makes `every_reload` mean "every page load" rather than
   * "every refetch". The notification list re-renders on every poll, socket
   * arrival and cache write, so without a per-mount guard an `every_reload`
   * announcement would re-open the modal several times a minute. A fresh mount
   * (a real page load) starts with an empty ref, which is precisely the
   * semantics the option promises.
   */
  const replayedIdsRef = useRef<Set<string>>(new Set());

  /**
   * Put an announcement on screen, or queue it behind one already showing.
   *
   * The frequency mark is recorded HERE, at the moment it is actually shown —
   * never at receive time. An announcement that was parsed and then dropped
   * (expired, queue overflow) must not burn the single chance a `once_ever`
   * send gets.
   */
  const show = useCallback((announcement: AdminAnnouncement) => {
    recordModalShown(announcement.id, announcement.frequency);
    setCurrent((activeAnnouncement) => {
      if (activeAnnouncement) {
        // Something is already on screen — queue this one instead of
        // clobbering a message the user has not read yet.
        setQueue((prev) => enqueueAnnouncement(prev, announcement));
        return activeAnnouncement;
      }
      return announcement;
    });
  }, []);

  // Keep the user id in a ref so the socket subscription does not need it as a
  // dependency. Re-subscribing on every auth-object identity change would
  // detach the listener for a tick and could drop an announcement mid-flight.
  const userIdRef = useRef<string | null>(null);
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user?.id]);

  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleAnnouncement = (payload: unknown) => {
      if (!isAnnouncementForUser(payload, userIdRef.current)) return;

      const announcement = parseAnnouncementPayload(payload);
      if (!announcement) return;

      if (!shouldShowAnnouncement(announcement.id, seenIdsRef.current, Date.now())) return;

      // Past its advisory expiry. A scheduled send can legitimately fire after
      // the expiry the admin typed, and a socket can deliver late — either way
      // interrupting someone with a message that has lapsed is worse than
      // silence. The bell row (if any) is still written by the backend.
      if (isAnnouncementExpired(announcement)) return;

      // The admin's re-show budget for this device. `every_reload` records
      // nothing and so always passes; the other two consult storage.
      if (!shouldOpenModalForFrequency(announcement.id, announcement.frequency)) return;

      // A live delivery counts as this mount's showing, so the replay pass below
      // does not immediately open the same modal a second time.
      replayedIdsRef.current.add(announcement.id);
      show(announcement);
    };

    socket.on(ADMIN_ANNOUNCEMENT_EVENT, handleAnnouncement);
    return () => {
      socket.off(ADMIN_ANNOUNCEMENT_EVENT, handleAnnouncement);
    };
  }, [socket, isConnected, show]);

  /**
   * Re-open the modal from the PERSISTED bell row on a later page load.
   *
   * 🔴 This is the only reason `every_reload` / `once_per_session` can mean
   * anything. The `admin_announcement` socket event is transient by design — a
   * refresh loses an undismissed popup — so a frequency option that only ever
   * saw one live emit could never re-show. The In-App channel is the one channel
   * with a durable row to replay FROM, which is why this is scoped to it.
   *
   * Every gate below is a refusal to invent a modal that was never asked for:
   *  - `modalRequested` — the send actually ticked "pop a modal". A bell-only
   *    announcement must never grow one retroactively.
   *  - `isForCurrentSurface` — same device gate the bell already applies.
   *  - expiry — a lapsed announcement is not re-offered.
   *  - the frequency mark — `once_ever` / `once_per_session` stop here.
   *  - `replayedIdsRef` — at most once per mount, whatever the frequency.
   */
  useEffect(() => {
    if (!Array.isArray(notifications) || notifications.length === 0) return;

    for (const notification of notifications) {
      const inApp = inAppAnnouncementOf(notification);
      if (!inApp || !inApp.modalRequested) continue;
      if (!isForCurrentSurface(inApp)) continue;
      if (isInAppAnnouncementExpired(inApp)) continue;

      // The announcement id is what the frequency marks are keyed on, and it is
      // the SAME id the live socket payload carries — the backend passes its
      // `announcementId` into both. Falling back to the row id would let one
      // announcement be shown twice under two different keys.
      const id = inApp.announcementId;
      if (!id) continue;
      if (replayedIdsRef.current.has(id)) continue;
      if (!shouldOpenModalForFrequency(id, inApp.modalFrequency)) continue;

      replayedIdsRef.current.add(id);
      show({
        id,
        channel: 'in_app_notification',
        title: String((notification as { title?: unknown })?.title || ''),
        // The flattened one-liner, NOT `inApp.body` — this modal renders TEXT and
        // the body is Markdown source. Rendering the source here would show
        // literal `**` and `![alt](url)`; rendering it as HTML is the injection
        // surface the whole channel is designed to avoid.
        message: String((notification as { message?: unknown })?.message || ''),
        imageUrl: inApp.media.find((m) => m.type === 'image')?.url ?? null,
        actionUrl: inApp.actionUrl,
        sentAt: inApp.sentAt || '',
        sentByEmail: inApp.sentByEmail,
        severity: inApp.severity,
        expiresAt: inApp.expiresAt,
        frequency: inApp.modalFrequency,
      });
    }
  }, [notifications, show]);

  /** Advance to the next queued announcement, or close. */
  const dismiss = useCallback(() => {
    setQueue((prev) => {
      if (prev.length === 0) {
        setCurrent(null);
        return prev;
      }
      const [next, ...rest] = prev;
      setCurrent(next);
      return rest;
    });
  }, []);

  const handleAction = useCallback(() => {
    const url = current?.actionUrl;
    if (!isSafeActionUrl(url)) {
      dismiss();
      return;
    }
    const target = String(url);
    if (isInternalActionUrl(target)) {
      navigate(target);
    } else {
      // External link: new tab, and `noopener` so the opened page cannot reach
      // back into this one via window.opener.
      window.open(target, '_blank', 'noopener,noreferrer');
    }
    dismiss();
  }, [current?.actionUrl, dismiss, navigate]);

  if (!current) return null;

  const showAction = isSafeActionUrl(current.actionUrl);
  const severity = SEVERITY_PRESENTATION[current.severity];
  const SeverityIcon = SEVERITY_ICON[current.severity];

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <DialogContent className="max-w-md" data-testid="admin-announcement-modal">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${severity.iconWrapClass}`}
              data-testid={`admin-announcement-severity-${current.severity}`}
            >
              <SeverityIcon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle
                className="text-left text-base leading-snug"
                data-testid="admin-announcement-title"
              >
                {announcementHeading(current)}
              </DialogTitle>
              {current.message ? (
                <DialogDescription
                  className="mt-1.5 whitespace-pre-wrap break-words text-left text-sm text-muted-foreground"
                  data-testid="admin-announcement-message"
                >
                  {current.message}
                </DialogDescription>
              ) : null}
            </div>
          </div>
        </DialogHeader>

        {current.imageUrl ? (
          <img
            src={current.imageUrl}
            alt=""
            className="max-h-48 w-full rounded-md object-cover"
            // A broken/unreachable admin-supplied URL must not leave a torn
            // image icon sitting in the middle of the modal.
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : null}

        <DialogFooter className="gap-2 sm:justify-end">
          {queue.length > 0 ? (
            <span
              className="mr-auto self-center text-xs text-muted-foreground"
              data-testid="admin-announcement-queue-count"
            >
              +{queue.length} more
            </span>
          ) : null}
          <Button variant={showAction ? 'outline' : 'default'} onClick={dismiss} data-testid="admin-announcement-dismiss">
            {queue.length > 0 ? 'Next' : 'Dismiss'}
          </Button>
          {showAction ? (
            <Button onClick={handleAction} data-testid="admin-announcement-action">
              View
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminAnnouncementModal;
